import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";

import {
  EVENT_GROUPS_QUERY_ROOT,
  useEventGroupRepository,
} from "@/pages/event-groups/hooks/use-event-group-repository";

/**
 * A page's cache key. It sits under the cache root, so the repository's writes refresh it. Each
 * page has its own `scope`: the edit page must not seed its form from the show page's cached copy.
 */
export const eventGroupKey = (scope: "show" | "edit", id: string | undefined) =>
  [EVENT_GROUPS_QUERY_ROOT, scope, id] as const;

/** A 404 from the admin API: the group was deleted, or the id never existed. */
function isNotFound(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 404;
}

/** The group and its events. `data` is null once the group is gone; any other failure is the query's error. */
export function useEventGroupQuery(
  scope: "show" | "edit",
  id: string | undefined,
) {
  const repo = useEventGroupRepository();
  return useQuery({
    queryKey: eventGroupKey(scope, id),
    enabled: !!id,
    // The edit page seeds a form from the first answer and never re-seeds it, so it must not start
    // from a copy kept after the last visit: saving that would write over a newer save.
    gcTime: scope === "edit" ? 0 : undefined,
    queryFn: async () => {
      try {
        return await repo.get(id!);
      } catch (error) {
        if (isNotFound(error)) return null;
        throw error;
      }
    },
  });
}
